
import Vue from 'vue';
import Component from 'vue-class-component';
import UploadTray from '@/components/UploadTray.vue';
import GroupOperations from '@/components/GroupOperations.vue';
import { Prop, Watch } from 'vue-property-decorator';
import Photo from '../model/Photo';
import HelpCenter from '@/components/HelpCenter.vue';
import Placeholder from '@/model/Placeholder';

@Component({
    components: {
        UploadTray,
        GroupOperations,
        HelpCenter,
    },
})
export default class Footer extends Vue {
    @Prop({
        required: true,
        type: Boolean,
    })
    private showUploadTray!: boolean;

    @Prop({
        default: [],
        type: Array,
    })
    private selectedPhotos!: Array<Photo|Placeholder>;

    private changelogDialog = false;
    private shouldBounce = false;
    private bounceInterval: number | null = null;
    private audioFadeAnimation: number | null = null;
    private presentationAdvanceTimeout: number | null = null;
    private copyVisible = false;
    private isUploadTrayHovered = false;
    private uploadTrayFadeOut = false;
    private uploadTrayFadeOutTimeout: number | null = null;
    private uploadParticlesAnimation: number | null = null;
    private uploadParticles: Array<{x: number, y: number, vx: number, vy: number, size: number, opacity: number, life: number, maxLife: number, type: string}> = [];
    private uploadClickWaveVisible = false;
    private uploadClickWaveKey = 0;
    private uploadClickWaveX = 0;
    private uploadClickWaveY = 0;
    private uploadClickWaveSize = 0;
    private uploadClickWaveVariant: 'subtle' | 'strong' = 'subtle';
    private uploadClickWaveTimeout: number | null = null;
    private uiRippleListener: EventListener | null = null;
    private isLeavingSession = false;
    private sessionLeaveListener: EventListener | null = null;

    mounted() {
        this.startBounceInterval();
        this.sessionLeaveListener = () => {
            this.isLeavingSession = true;
            window.setTimeout(() => {
                this.isLeavingSession = false;
            }, 340);
        };
        window.addEventListener('raws-session-leave', this.sessionLeaveListener);
        this.uiRippleListener = (event: Event) => {
            const customEvent = event as CustomEvent<{ x?: number; y?: number }>;
            const x = customEvent.detail?.x;
            const y = customEvent.detail?.y;
            if (typeof x === 'number' && typeof y === 'number') {
                this.triggerClickWave({ x, y }, 'subtle');
            }
        };
        window.addEventListener('raws-ui-ripple', this.uiRippleListener);
        // Add ESC key listener
        document.addEventListener('keydown', this.handleKeydown);
    }

    beforeDestroy() {
        if (this.bounceInterval) {
            clearInterval(this.bounceInterval);
        }
        if (this.audioFadeAnimation) {
            cancelAnimationFrame(this.audioFadeAnimation);
        }
        if (this.presentationAdvanceTimeout) {
            clearTimeout(this.presentationAdvanceTimeout);
        }
        if (this.uploadParticlesAnimation) {
            cancelAnimationFrame(this.uploadParticlesAnimation);
        }
        if (this.uploadTrayFadeOutTimeout) {
            clearTimeout(this.uploadTrayFadeOutTimeout);
        }
        if (this.uploadClickWaveTimeout) {
            clearTimeout(this.uploadClickWaveTimeout);
            this.uploadClickWaveTimeout = null;
        }
        if (this.sessionLeaveListener) {
            window.removeEventListener('raws-session-leave', this.sessionLeaveListener);
            this.sessionLeaveListener = null;
        }
        if (this.uiRippleListener) {
            window.removeEventListener('raws-ui-ripple', this.uiRippleListener);
            this.uiRippleListener = null;
        }
        // Remove ESC key listener
        document.removeEventListener('keydown', this.handleKeydown);
    }
    @Watch('changelogDialog')
    onDialogChange(newVal: boolean) {
        if (!newVal) {
            if (this.presentationAdvanceTimeout) {
                clearTimeout(this.presentationAdvanceTimeout);
                this.presentationAdvanceTimeout = null;
            }
            // Modal is closing, stop the music
            const audio = this.$refs.changelogAudio as HTMLAudioElement;
            if (audio) {
                if (this.audioFadeAnimation) {
                    cancelAnimationFrame(this.audioFadeAnimation);
                    this.audioFadeAnimation = null;
                }
                audio.pause();
                audio.currentTime = 0;
            }
        }
    }

    private startBounceInterval() {
        this.bounceInterval = window.setInterval(() => {
            this.shouldBounce = true;
            setTimeout(() => {
                this.shouldBounce = false;
            }, 1000); // Bounce for 1 second
        }, 5000); // Every 5 seconds
    }

    private openChangelog() {
        this.changelogDialog = true;
        this.copyVisible = false;

        this.$nextTick(() => {
            if (!this.changelogDialog) return;
            const presentationStage = this.$refs.presentationStage as HTMLElement;
            if (presentationStage) {
                presentationStage.scrollTop = 0;
                if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
                    this.presentationAdvanceTimeout = window.setTimeout(() => this.goToStory(), 5700);
                }
            }

            const audio = this.$refs.changelogAudio as HTMLAudioElement;
            audio.volume = 0;
            audio.play().then(() => {
                this.fadeInChangelogAudio(audio);
            }).catch(() => {
                // Handle autoplay restrictions gracefully
            });
        });
    }

    private fadeInChangelogAudio(audio: HTMLAudioElement) {
        if (this.audioFadeAnimation) {
            cancelAnimationFrame(this.audioFadeAnimation);
        }

        const targetVolume = 0.3;
        const fadeDuration = 2200;
        const startedAt = performance.now();

        const updateVolume = (now: number) => {
            const progress = Math.min(1, (now - startedAt) / fadeDuration);
            const easedProgress = progress * progress * (3 - 2 * progress);
            audio.volume = targetVolume * easedProgress;

            if (progress < 1 && this.changelogDialog) {
                this.audioFadeAnimation = requestAnimationFrame(updateVolume);
                return;
            }

            this.audioFadeAnimation = null;
        };

        this.audioFadeAnimation = requestAnimationFrame(updateVolume);
    }

    private goToStory() {
        if (this.presentationAdvanceTimeout) {
            clearTimeout(this.presentationAdvanceTimeout);
            this.presentationAdvanceTimeout = null;
        }
        const stage = this.$refs.presentationStage as HTMLElement;
        const copySlide = this.$refs.presentationCopySlide as HTMLElement;
        if (stage && copySlide) {
            stage.scrollTo({
                top: copySlide.offsetTop,
                behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
            });
        }
    }

    private onPresentationScroll() {
        const stage = this.$refs.presentationStage as HTMLElement;
        if (!stage) return;
        if (stage.scrollTop > 24 && this.presentationAdvanceTimeout) {
            clearTimeout(this.presentationAdvanceTimeout);
            this.presentationAdvanceTimeout = null;
        }
        if (stage.scrollTop > stage.clientHeight * 0.35) {
            this.copyVisible = true;
        }
    }

    private closeModal() {
        this.changelogDialog = false;
    }

    private handleKeydown(event: KeyboardEvent) {
        if (event.key === 'Escape' && this.changelogDialog) {
            this.closeModal();
        }
    }

    private onUploadTrayHover() {
        if (this.uploadTrayFadeOutTimeout) {
            clearTimeout(this.uploadTrayFadeOutTimeout);
            this.uploadTrayFadeOutTimeout = null;
        }
        this.uploadTrayFadeOut = false;
        this.isUploadTrayHovered = true;
        this.initUploadParticles();
    }

    private onUploadTrayLeave() {
        this.isUploadTrayHovered = false;
        this.uploadTrayFadeOut = true;

        if (this.uploadTrayFadeOutTimeout) {
            clearTimeout(this.uploadTrayFadeOutTimeout);
        }

        this.uploadTrayFadeOutTimeout = window.setTimeout(() => {
            this.uploadTrayFadeOut = false;
            this.stopUploadParticles();
            this.uploadTrayFadeOutTimeout = null;
        }, 2000);
    }

    private onUploadTrayClick(payload: { x: number, y: number }) {
        this.triggerClickWave(payload, 'strong');
    }

    private triggerClickWave(payload: { x: number, y: number }, variant: 'subtle' | 'strong') {
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const distanceToTopLeft = Math.hypot(payload.x, payload.y);
        const distanceToTopRight = Math.hypot(viewportWidth - payload.x, payload.y);
        const distanceToBottomLeft = Math.hypot(payload.x, viewportHeight - payload.y);
        const distanceToBottomRight = Math.hypot(viewportWidth - payload.x, viewportHeight - payload.y);
        const maxDistance = Math.max(distanceToTopLeft, distanceToTopRight, distanceToBottomLeft, distanceToBottomRight);

        this.uploadClickWaveX = payload.x;
        this.uploadClickWaveY = payload.y;
        this.uploadClickWaveSize = maxDistance * 2;
        this.uploadClickWaveVariant = variant;
        this.uploadClickWaveVisible = false;
        this.uploadClickWaveKey += 1;

        this.$nextTick(() => {
            this.uploadClickWaveVisible = true;
        });

        if (this.uploadClickWaveTimeout) {
            clearTimeout(this.uploadClickWaveTimeout);
        }

        this.uploadClickWaveTimeout = window.setTimeout(() => {
            this.uploadClickWaveVisible = false;
            this.uploadClickWaveTimeout = null;
        }, 2200);
    }

    get uploadClickWaveStyle() {
        return {
            left: `${this.uploadClickWaveX}px`,
            top: `${this.uploadClickWaveY}px`,
            width: `${this.uploadClickWaveSize}px`,
            height: `${this.uploadClickWaveSize}px`,
            '--wave-core-alpha': this.uploadClickWaveVariant === 'strong' ? '0.15' : '0.08',
            '--wave-mid-alpha': this.uploadClickWaveVariant === 'strong' ? '0.085' : '0.045',
            '--wave-start-opacity': this.uploadClickWaveVariant === 'strong' ? '0.24' : '0.14',
            '--wave-mid-opacity': this.uploadClickWaveVariant === 'strong' ? '0.1' : '0.06',
            '--wave-late-opacity': this.uploadClickWaveVariant === 'strong' ? '0.04' : '0.02',
        };
    }

    private initUploadParticles() {
        this.stopUploadParticles();

        const canvas = this.$refs.uploadParticles as HTMLCanvasElement;
        if (!canvas) return;

        // Set canvas size to cover bottom 400px
        canvas.width = window.innerWidth;
        canvas.height = 400;

        // Initialize particles array
        this.uploadParticles = [];

        this.animateUploadParticles();
    }

    private stopUploadParticles() {
        if (this.uploadParticlesAnimation) {
            cancelAnimationFrame(this.uploadParticlesAnimation);
            this.uploadParticlesAnimation = null;
        }
        this.uploadParticles = [];
    }

    private animateUploadParticles() {
        const canvas = this.$refs.uploadParticles as HTMLCanvasElement;
        if (!canvas || (!this.isUploadTrayHovered && !this.uploadTrayFadeOut)) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        // Clear canvas with transparent background
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        // Spawn new particles only when hovering (not during fade out)
        if (this.isUploadTrayHovered && Math.random() < 0.3) {
            this.spawnUploadParticle();
        }

        // Update and draw particles
        this.uploadParticles = this.uploadParticles.filter(particle => {
            // Update position
            particle.x += particle.vx;
            particle.y += particle.vy;
            particle.life++;

            // Apply gravity and slight randomness (slower)
            particle.vy -= 0.008; // Slower float upward
            particle.vx += (Math.random() - 0.5) * 0.005; // Slower horizontal drift

            // Fade out as particle ages
            particle.opacity = Math.max(0, 1 - (particle.life / particle.maxLife));

            // Draw particle based on type
            this.drawUploadParticle(ctx, particle);

            // Remove if life expired or off screen
            return particle.life < particle.maxLife && 
                   particle.x > -50 && particle.x < canvas.width + 50 && 
                   particle.y > -50 && particle.y < canvas.height + 50;
        });

        this.uploadParticlesAnimation = requestAnimationFrame(() => this.animateUploadParticles());
    }

    private spawnUploadParticle() {
        const canvas = this.$refs.uploadParticles as HTMLCanvasElement;
        if (!canvas) return;

        const types = ['star', 'circle', 'square', 'triangle'];
        const particle = {
            x: Math.random() * canvas.width,
            y: canvas.height + 20, // Start from bottom
            vx: (Math.random() - 0.5) * 0.8, // Slower horizontal velocity
            vy: -Math.random() * 0.8 - 0.3, // Slower upward velocity
            size: Math.random() * 4 + 2,
            opacity: 1,
            life: 0,
            maxLife: 200 + Math.random() * 150, // 200-350 frames (longer life for slower movement)
            type: types[Math.floor(Math.random() * types.length)]
        };

        this.uploadParticles.push(particle);
    }

    private drawUploadParticle(ctx: CanvasRenderingContext2D, particle: any) {
        ctx.save();
        ctx.globalAlpha = particle.opacity;
        ctx.translate(particle.x, particle.y);

        const halfSize = particle.size / 2;
        
        switch (particle.type) {
            case 'star':
                ctx.beginPath();
                for (let i = 0; i < 5; i++) {
                    const angle = (i * Math.PI * 2) / 5 - Math.PI / 2;
                    const x = Math.cos(angle) * halfSize;
                    const y = Math.sin(angle) * halfSize;
                    if (i === 0) { ctx.moveTo(x, y); } else { ctx.lineTo(x, y); }
                    
                    const innerAngle = angle + Math.PI / 5;
                    const innerX = Math.cos(innerAngle) * (halfSize * 0.4);
                    const innerY = Math.sin(innerAngle) * (halfSize * 0.4);
                    ctx.lineTo(innerX, innerY);
                }
                ctx.closePath();
                ctx.fillStyle = '#ff4081';
                ctx.fill();
                break;
                
            case 'circle':
                ctx.beginPath();
                ctx.arc(0, 0, halfSize, 0, Math.PI * 2);
                ctx.fillStyle = '#ff4081';
                ctx.fill();
                break;
                
            case 'square':
                ctx.fillStyle = '#ff4081';
                ctx.fillRect(-halfSize, -halfSize, particle.size, particle.size);
                break;
                
            case 'triangle':
                ctx.beginPath();
                ctx.moveTo(0, -halfSize);
                ctx.lineTo(-halfSize, halfSize);
                ctx.lineTo(halfSize, halfSize);
                ctx.closePath();
                ctx.fillStyle = '#ff4081';
                ctx.fill();
                break;
        }

        ctx.restore();
    }


}
